import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ShieldCheck, Spinner, ArrowsClockwise, Plus, Trash, PencilSimple,
  X, MagnifyingGlass, Buildings, Warning, FloppyDisk, UsersThree,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { fetchPositions, createPosition, updatePosition, deletePosition } from "@/lib/api";
import AccountMenu from "@/components/AccountMenu";
import { useT } from "@/lib/i18n";

const EMPTY_FORM = {
  ep_id: null,
  department: "",
  position_name: "",
  title: "",
  report_to: "",
  duties: "",
  authorities: "",
  explanation: "",
  prerequisites: "",
  employing_company: "",
};

/**
 * /admin/positions — staff-only Employee Positions CRUD.
 */
export default function PositionsAdminPage({ user, onLogout, onLogoutAll }) {
  const navigate = useNavigate();
  const tx = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [error, setError] = useState("");
  const [form, setForm] = useState(null);    // null = closed, object = open
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async (qStr = "") => {
    setLoading(true); setError("");
    try {
      const r = await fetchPositions(qStr);
      setItems(r.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not load positions");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    const t = setTimeout(() => load(q), q ? 250 : 0);
    return () => clearTimeout(t);
  }, [q, load]);

  const onAdd = () => setForm({ ...EMPTY_FORM });
  const onEdit = (it) => setForm({ ...EMPTY_FORM, ...it });
  const onClose = () => setForm(null);

  const onSave = async () => {
    if (!form?.department || !form?.position_name || !form?.title) return;
    setSaving(true);
    try {
      if (form.ep_id) {
        await updatePosition(form.ep_id, form);
      } else {
        await createPosition(form);
      }
      setForm(null);
      await load(q);
    } catch (e) {
      const msg = typeof e?.response?.data?.detail === "object"
        ? (e.response.data.detail.message || JSON.stringify(e.response.data.detail))
        : (e?.response?.data?.detail || e?.message || "Save failed");
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (it) => {
    if (!window.confirm(`Delete position "${it.position_name}"? This can't be undone.`)) return;
    setDeletingId(it.ep_id);
    try {
      await deletePosition(it.ep_id);
      await load(q);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "POSITION_IN_USE") {
        setError(`Cannot delete: ${detail.in_use_count} person(s) still hold this position. Reassign them first.`);
      } else {
        setError(detail?.message || detail || e?.message || "Delete failed");
      }
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="positions-page">
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 md:px-8 h-14 flex items-center gap-3">
          <button onClick={() => navigate("/")} className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500" data-testid="positions-back">
            <ArrowLeft size={18} weight="bold" />
          </button>
          <UsersThree size={20} weight="fill" className="text-blue-700" />
          <h1 className="text-base md:text-lg font-black tracking-tighter font-['Chivo']">{tx("positions.title", "Employee Positions")}</h1>
          <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 rounded-sm">Admin</span>
          <button onClick={() => load(q)} disabled={loading} className="ml-auto inline-flex items-center gap-1 text-xs px-2 py-1 border border-slate-300 rounded-sm hover:bg-slate-100 disabled:opacity-50" data-testid="positions-refresh">
            {loading ? <Spinner size={12} className="animate-spin" /> : <ArrowsClockwise size={12} />}
            {tx("action.refresh", "Refresh")}
          </button>
          <button onClick={onAdd} className="inline-flex items-center gap-1 text-xs px-3 py-1 bg-blue-700 text-white border border-blue-800 rounded-sm hover:bg-blue-800" data-testid="positions-new">
            <Plus size={12} weight="bold" /> New
          </button>
          <AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} />
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 md:p-8">
        <div className="relative mb-3">
          <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tx("positions.search_placeholder", "Search department / position / title…")} className="pl-7 h-9 text-sm" data-testid="positions-search" />
        </div>

        {error && (
          <div className="bg-rose-50 border border-rose-200 text-rose-800 text-sm rounded-sm px-3 py-2 mb-3 flex items-start gap-2" data-testid="positions-error">
            <Warning size={14} weight="fill" className="text-rose-600 shrink-0 mt-0.5" />
            <span className="flex-1">{String(error)}</span>
            <button onClick={() => setError("")} className="text-rose-500 hover:text-rose-700"><X size={12} weight="bold" /></button>
          </div>
        )}

        {loading && !items.length ? (
          <div className="bg-white rounded-md border border-slate-200 p-8 text-center"><Spinner size={20} className="animate-spin text-slate-400 mx-auto mb-1" /></div>
        ) : !items.length ? (
          <div className="bg-white rounded-md border border-slate-200 p-12 text-center text-sm text-slate-500" data-testid="positions-empty">
            {q ? "No positions match." : "No positions defined yet."}
          </div>
        ) : (
          <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <Th>Department</Th><Th>Position</Th><Th>Title</Th>
                  <Th>{tx("positions.reports_to", "Reports to")}</Th><Th>Company</Th><Th></Th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.ep_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`positions-row-${it.ep_id}`}>
                    <td className="px-3 py-2"><span className="text-slate-600 truncate">{it.department || "—"}</span></td>
                    <td className="px-3 py-2 font-bold text-slate-800">{it.position_name || "—"}</td>
                    <td className="px-3 py-2 text-slate-700">{it.title || "—"}</td>
                    <td className="px-3 py-2 text-slate-500 font-mono text-[10px]">{it.report_to || "—"}</td>
                    <td className="px-3 py-2"><span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-sm"><Buildings size={9} weight="fill" />{it.employing_company || "—"}</span></td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button onClick={() => onEdit(it)} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-blue-700 hover:text-blue-900 mr-2" data-testid={`positions-edit-${it.ep_id}`}><PencilSimple size={11} weight="fill" /> Edit</button>
                      <button onClick={() => onDelete(it)} disabled={deletingId === it.ep_id} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-rose-700 hover:text-rose-900 disabled:opacity-40" data-testid={`positions-delete-${it.ep_id}`}>{deletingId === it.ep_id ? <Spinner size={11} className="animate-spin" /> : <Trash size={11} weight="fill" />} {tx("action.delete", "Delete")}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-3 text-[11px] text-slate-500 flex items-center gap-1">
          <ShieldCheck size={12} weight="fill" />
          Positions cannot be deleted while one or more <code>Persons_for_Customer</code> rows still reference them.
        </div>
      </main>

      {form && <PositionEditor form={form} setForm={setForm} onSave={onSave} onClose={onClose} saving={saving} />}
    </div>
  );
}

function PositionEditor({ form, setForm, onSave, onClose, saving }) {
  const tx = useT();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isNew = !form.ep_id;
  const valid = form.department && form.position_name && form.title;
  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/50 flex items-end md:items-center justify-center p-0 md:p-6" onClick={onClose} data-testid="positions-editor">
      <div className="bg-white rounded-t-md md:rounded-md w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 bg-slate-50">
          <UsersThree size={18} weight="fill" className="text-blue-700" />
          <h3 className="text-sm font-bold tracking-tight font-['Chivo'] flex-1">{isNew ? "New position" : "Edit position"}</h3>
          {form.ep_id && <span className="font-mono text-[10px] text-slate-400">{form.ep_id}</span>}
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-sm hover:bg-slate-200 text-slate-500" data-testid="positions-editor-close"><X size={14} weight="bold" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <Field label="Department *"><Input value={form.department} onChange={set("department")} className="text-sm" data-testid="positions-field-department" /></Field>
          <Field label={tx("positions.field_position_name", "Position Name *")}><Input value={form.position_name} onChange={set("position_name")} className="text-sm" data-testid="positions-field-name" /></Field>
          <Field label="Title *"><Input value={form.title} onChange={set("title")} className="text-sm" data-testid="positions-field-title" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={tx("positions.field_reports_to", "Reports to (EP_ID)")}><Input value={form.report_to || ""} onChange={set("report_to")} className="text-sm font-mono" placeholder="optional" data-testid="positions-field-reportto" /></Field>
            <Field label={tx("positions.field_employing_company", "Employing Company")}><Input value={form.employing_company || ""} onChange={set("employing_company")} className="text-sm" data-testid="positions-field-company" /></Field>
          </div>
          <Field label="Duties"><textarea value={form.duties || ""} onChange={set("duties")} className="w-full text-sm p-2 border border-slate-300 rounded-sm min-h-[60px]" data-testid="positions-field-duties" /></Field>
          <Field label="Authorities"><textarea value={form.authorities || ""} onChange={set("authorities")} className="w-full text-sm p-2 border border-slate-300 rounded-sm min-h-[60px]" data-testid="positions-field-authorities" /></Field>
          <Field label="Explanation"><textarea value={form.explanation || ""} onChange={set("explanation")} className="w-full text-sm p-2 border border-slate-300 rounded-sm min-h-[60px]" data-testid="positions-field-explanation" /></Field>
          <Field label="Prerequisites"><textarea value={form.prerequisites || ""} onChange={set("prerequisites")} className="w-full text-sm p-2 border border-slate-300 rounded-sm min-h-[60px]" data-testid="positions-field-prerequisites" /></Field>
        </div>
        <div className="border-t border-slate-200 p-3 bg-slate-50 flex flex-col sm:flex-row gap-2 sm:justify-end">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-white border border-slate-300 text-slate-700 rounded-sm hover:bg-slate-50">{tx("action.cancel", "Cancel")}</button>
          <button onClick={onSave} disabled={saving || !valid} className="px-5 py-2 text-xs font-bold uppercase tracking-wider bg-blue-700 text-white rounded-sm hover:bg-blue-800 disabled:bg-slate-300 inline-flex items-center justify-center gap-1.5" data-testid="positions-save">
            {saving ? <><Spinner size={12} className="animate-spin" /> Saving…</> : <><FloppyDisk size={12} weight="fill" /> Save</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}

function Th({ children }) {
  return <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 text-left">{children}</th>;
}
